import { useEffect, useState } from 'react';
import { useSession } from '../../app/current.tsx';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import type { Period } from './period.ts';

export interface ReportTotals {
  sales_count: number;
  returns_count: number;
  total: number;
  sales_total: number;
  returns_total: number;
  discounts: number;
  adjustments: number;
  tax: number;
  items_total: number;
  revenue_with_cost: number;
  cost: number;
  profit: number;
  qty_milli: number;
  products_without_cost: number;
  revenue_without_cost: number;
}

export interface ReportSummary {
  timezone: string;
  totals: ReportTotals;
  by_day: Array<{ day: string; total: number; count: number; profit: number }>;
  by_hour: Array<{ hour: number; total: number; count: number }>;
  by_method: Array<{ method: string; total: number; count: number }>;
  by_seller: Array<{ seller_id: string | null; name: string; total: number; count: number; discounts: number }>;
  by_category: Array<{ category_id: string | null; name: string; total: number; qty_milli: number; profit: number }>;
}

export interface TopProduct {
  variant_id: string;
  product: string;
  attributes: Record<string, string>;
  unit_type: 'unit' | 'weight' | 'volume' | 'length';
  qty_milli: number;
  total: number;
  profit: number | null;
}

/** Los importes llegan de Postgres como texto (bigint): se pasan a número. */
function numbers<T>(value: T): T {
  if (Array.isArray(value)) return value.map(numbers) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) && k !== 'day' ? Number(v) : numbers(v),
      ]),
    ) as T;
  }
  return value;
}

export async function fetchSummary(businessId: string, period: Pick<Period, 'from' | 'to'>): Promise<ReportSummary> {
  const { data, error } = await supabase.rpc('report_summary', { p_business_id: businessId, p_from: period.from, p_to: period.to });
  if (error) throw new Error(error.message);
  return numbers(data as ReportSummary);
}

export async function fetchTopProducts(businessId: string, period: Pick<Period, 'from' | 'to'>, order: 'total' | 'qty' | 'profit', limit = 50): Promise<TopProduct[]> {
  const { data, error } = await supabase.rpc('report_top_products', { p_business_id: businessId, p_from: period.from, p_to: period.to, p_order: order, p_limit: limit });
  if (error) throw new Error(error.message);
  return numbers(data as TopProduct[]);
}

export type Loadable<T> = { state: 'loading' } | { state: 'offline' } | { state: 'error'; message: string } | { state: 'ready'; data: T };

/** Carga un reporte del servidor (los reportes necesitan conexión: suman todos los dispositivos). */
export function useReport<T>(load: (businessId: string) => Promise<T>, deps: unknown[]): Loadable<T> {
  const { business } = useSession();
  const online = useOnline();
  const [value, setValue] = useState<Loadable<T>>({ state: 'loading' });
  useEffect(() => {
    if (!online) {
      setValue({ state: 'offline' });
      return;
    }
    let cancelled = false;
    setValue({ state: 'loading' });
    load(business.id)
      .then((data) => !cancelled && setValue({ state: 'ready', data }))
      .catch((e: unknown) => !cancelled && setValue({ state: 'error', message: e instanceof Error ? e.message : String(e) }));
    return () => {
      cancelled = true;
    };
  }, [business.id, online, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return value;
}

/** Completa los días sin ventas con cero (el gráfico muestra el período entero). */
export function fillDays(period: Period, byDay: ReportSummary['by_day']): ReportSummary['by_day'] {
  const map = new Map(byDay.map((d) => [d.day, d]));
  return period.days.map((day) => map.get(day) ?? { day, total: 0, count: 0, profit: 0 });
}
